import { useEffect } from "react";
import { applyReciprocalHreflang, canonicalPathname, clearOwnedHreflang } from "../data/seo/hreflangLinks";
import { absolutePublicAssetUrl, isIndiaSite, localizePublicAssetUrl } from "../data/seo/siteEntity";
import { applyRobotsMeta, captureRobotsMeta, INDEXABLE_ROBOTS, NOINDEX_ROBOTS, restoreRobotsMeta } from "./robotsMeta";

export { INDEXABLE_ROBOTS, NOINDEX_ROBOTS, applyRobotsMeta, captureRobotsMeta, restoreRobotsMeta };

interface PageSeoOptions {
    /** Final <title> for the page. Recommended: 50–60 chars including brand suffix. */
    title?: string;
    /** Meta description. Recommended: 150–160 chars. */
    description?: string;
    /** Comma-separated keywords. Mostly ignored by Google; still used by Yandex/Baidu/AEO. */
    keywords?: string;
    /** Absolute or path-only canonical URL. Path-only values use the build's public origin. */
    canonical?: string;
    /** Absolute or path-only OG image URL. Should be 1200×630. */
    ogImage?: string;
    /** OpenGraph type — defaults to `website`. Use `article` on blog posts. */
    ogType?: "website" | "article" | "product" | "profile";
    /**
     * When true, the route must not be indexed (auth, print, not-found).
     * Indexable callers omit this; the hook then opts the document back into
     * `index, follow`, which matters because the SPA shell in `index.html` is
     * `noindex` so non-prerendered URLs are not treated as the homepage.
     */
    noindex?: boolean;
    /**
     * When false, the page stays out of the en / en-IN / x-default cluster.
     * Defaults to true for indexable pages. noindex always suppresses it.
     */
    hreflang?: boolean;
}

const SITE_NAME = "FloGPT";

/**
 * Marker set on <html> whenever a page applies its own title/description.
 * `MetaTagsManager` reads it and skips its site-wide DB defaults so per-page
 * SEO always wins, even when the system-settings query resolves AFTER this
 * hook ran (which is the norm during static prerender).
 */
export const PAGE_SEO_ATTR = "data-page-seo";

/**
 * Path-only URLs are joined to the build origin. The default build uses
 * window.location.origin (empty outside the browser, matching the previous
 * helper). The India build always uses https://flogpt.in, and rewrites an
 * absolute flogpt.com URL onto that host so a copied canonical cannot
 * keep the India page consolidated into .com.
 */
const toAbsolute = (value: string | undefined): string | undefined => {
    if (!value) return undefined;
    if (/^https?:\/\//i.test(value)) return localizePublicAssetUrl(value);
    const origin = isIndiaSite() ? "https://flogpt.in" : typeof window === "undefined" ? "" : window.location.origin;
    return `${origin}${value.startsWith("/") ? "" : "/"}${value}`;
};

const upsertMetaName = (name: string, content: string): HTMLMetaElement | null => {
    if (!content) return null;
    let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
    }
    tag.content = content;
    return tag;
};

const upsertMetaProperty = (property: string, content: string): HTMLMetaElement | null => {
    if (!content) return null;
    let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
    }
    tag.content = content;
    return tag;
};

const upsertCanonical = (href: string): HTMLLinkElement | null => {
    if (!href) return null;
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
    }
    link.href = href;
    return link;
};

/**
 * usePageSeo — set per-page <title>, description, canonical, OpenGraph, and
 * Twitter Card meta tags, restoring the previous values on unmount.
 *
 * Replaces the duplicated `useEffect + getOrCreateMetaTag` blocks in every
 * marketing page with a single hook. The previous-value capture means that
 * when the user navigates away, the homepage's tags from `index.html` /
 * `MetaTagsManager` come back instead of being stuck on the last page.
 *
 * Per-page <title> always wins because:
 *   1. Page mounts → this hook runs → sets per-page title.
 *   2. `MetaTagsManager` defaults effect uses a `hasAppliedDefaultsRef` and
 *      no longer overwrites title/description on settings load.
 *
 * Use alongside `<JsonLd>` blocks for schema.org structured data.
 */
const usePageSeo = ({
    title,
    description,
    keywords,
    canonical,
    ogImage,
    ogType = "website",
    noindex = false,
    hreflang = true,
}: PageSeoOptions) => {
    useEffect(() => {
        if (typeof document === "undefined") return;

        const previousTitle = document.title;
        const descTag = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
        const previousDescription = descTag?.content ?? null;
        const keywordsTag = document.querySelector('meta[name="keywords"]') as HTMLMetaElement | null;
        const previousKeywords = keywordsTag?.content ?? null;
        const canonicalTag = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
        const previousCanonical = canonicalTag?.href ?? null;
        const ogTitleTag = document.querySelector('meta[property="og:title"]') as HTMLMetaElement | null;
        const previousOgTitle = ogTitleTag?.content ?? null;
        const ogDescTag = document.querySelector('meta[property="og:description"]') as HTMLMetaElement | null;
        const previousOgDesc = ogDescTag?.content ?? null;
        const ogImageTag = document.querySelector('meta[property="og:image"]') as HTMLMetaElement | null;
        const previousOgImage = ogImageTag?.content ?? null;
        const ogTypeTag = document.querySelector('meta[property="og:type"]') as HTMLMetaElement | null;
        const previousOgType = ogTypeTag?.content ?? null;
        const twTitleTag = document.querySelector('meta[name="twitter:title"]') as HTMLMetaElement | null;
        const previousTwTitle = twTitleTag?.content ?? null;
        const twDescTag = document.querySelector('meta[name="twitter:description"]') as HTMLMetaElement | null;
        const previousTwDesc = twDescTag?.content ?? null;
        const twImageTag = document.querySelector('meta[name="twitter:image"]') as HTMLMetaElement | null;
        const previousTwImage = twImageTag?.content ?? null;
        const twCardTag = document.querySelector('meta[name="twitter:card"]') as HTMLMetaElement | null;
        const previousTwCard = twCardTag?.content ?? null;
        const robotsSnapshot = captureRobotsMeta();
        // Drop alternates inherited from a previous route, then (for indexable
        // pages that opt into the cluster) write en, en-IN, and x-default for
        // THIS path. noindex routes stay without hreflang so auth and 404 URLs
        // are not advertised as locale variants. `/blogs` opts out until India
        // has a published post.
        const hreflangLinks = Array.from(document.querySelectorAll('link[rel="alternate"][hreflang]'));
        hreflangLinks.forEach((el) => el.remove());
        clearOwnedHreflang();

        if (title || description || noindex) {
            document.documentElement.setAttribute(PAGE_SEO_ATTR, "true");
        }
        applyRobotsMeta(noindex ? NOINDEX_ROBOTS : INDEXABLE_ROBOTS);
        upsertMetaName("twitter:card", "summary_large_image");
        if (title) {
            document.title = title;
            upsertMetaProperty("og:title", title);
            upsertMetaName("twitter:title", title);
        }
        if (description) {
            upsertMetaName("description", description);
            upsertMetaProperty("og:description", description);
            upsertMetaName("twitter:description", description);
        }
        if (keywords) {
            upsertMetaName("keywords", keywords);
        }
        if (canonical) {
            const fullCanonical = toAbsolute(canonical);
            if (fullCanonical) {
                upsertCanonical(fullCanonical);
                upsertMetaProperty("og:url", fullCanonical);
                if (!noindex && hreflang) applyReciprocalHreflang(canonicalPathname(fullCanonical));
            }
        }
        if (ogImage) {
            const fullOgImage = absolutePublicAssetUrl(ogImage);
            if (fullOgImage) {
                upsertMetaProperty("og:image", fullOgImage);
                upsertMetaName("twitter:image", fullOgImage);
            }
        }
        if (ogType) {
            upsertMetaProperty("og:type", ogType);
        }
        upsertMetaProperty("og:site_name", SITE_NAME);

        return () => {
            document.documentElement.removeAttribute(PAGE_SEO_ATTR);
            document.title = previousTitle;
            if (descTag && previousDescription !== null) descTag.content = previousDescription;
            if (keywordsTag && previousKeywords !== null) keywordsTag.content = previousKeywords;
            if (canonicalTag && previousCanonical !== null) canonicalTag.href = previousCanonical;
            if (ogTitleTag && previousOgTitle !== null) ogTitleTag.content = previousOgTitle;
            if (ogDescTag && previousOgDesc !== null) ogDescTag.content = previousOgDesc;
            if (ogImageTag && previousOgImage !== null) ogImageTag.content = previousOgImage;
            if (ogTypeTag && previousOgType !== null) ogTypeTag.content = previousOgType;
            if (twTitleTag && previousTwTitle !== null) twTitleTag.content = previousTwTitle;
            if (twDescTag && previousTwDesc !== null) twDescTag.content = previousTwDesc;
            if (twImageTag && previousTwImage !== null) twImageTag.content = previousTwImage;
            if (twCardTag && previousTwCard !== null) twCardTag.content = previousTwCard;
            else if (!twCardTag) document.querySelector('meta[name="twitter:card"]')?.remove();
            restoreRobotsMeta(robotsSnapshot);
            clearOwnedHreflang();
            // Put back only the alternates this page removed. The pair we
            // inserted is already gone, so it is not restored onto the next route.
            hreflangLinks.forEach((el) => {
                if (!el.isConnected) document.head.appendChild(el);
            });
        };
    }, [title, description, keywords, canonical, ogImage, ogType, noindex, hreflang]);
};

export default usePageSeo;
